import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { AlertCircleIcon } from 'lucide-react-native';
import { useCallback, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import TransitionScreen from '@/components/transitionScreen';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { submitPublicQuestion } from '@/services/submitPublicQuestion';
import { segmentClient } from '@/util/analytics';
import { getPlatformStorage } from '@/util/platformStorage';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5 web:px-0' });

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof formSchema>;

function NewQuestionGetContactInformationContent() {
  const { email: emailParam } = useLocalSearchParams<{ email?: string }>();

  const { navigate, replace } = useRouter();

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const toast = useToaster();

  const hasTrackedView = useRef(false);

  const [emailExistsError, setEmailExistsError] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      email: emailParam ?? '',
    },
    resolver: zodResolver(formSchema),
  });

  const { login: loginUser } = useAuth();

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: submitPublicQuestion,
  });

  useFocusEffect(
    useCallback(() => {
      if (hasTrackedView.current) {
        return;
      }

      hasTrackedView.current = true;

      segmentClient.track(ANALYTICS_EVENTS.NEW_QUESTION_GET_CONTACT_INFORMATION_VIEWED);
    }, [])
  );

  const onSubmit = handleSubmit(async (formData) => {
    try {
      setEmailExistsError(false);

      const storage = getPlatformStorage(undefined, 'local');

      const message = await storage.getItem(STORAGE_KEYS.NEW_QUESTION_DRAFT);

      if (!message || message.trim().length < 50) {
        toast.showError({ title: 'Please go back and enter your question first.' });

        return;
      }

      setIsSubmitting(true);

      const result = await submitQuestion({
        email: formData.email.trim().toLowerCase(),
        message: message.trim(),
      });

      if ('code' in result && result.code === 'EMAIL_EXISTS') {
        setIsSubmitting(false);

        setEmailExistsError(true);

        setError('email', {
          message: 'This email address already has an account.',
        });

        return;
      }

      if (!result.success || !('data' in result)) {
        setIsSubmitting(false);

        return;
      }

      segmentClient.track(ANALYTICS_EVENTS.NEW_QUESTION_GET_CONTACT_INFORMATION_SUBMITTED);

      await storage.removeItem(STORAGE_KEYS.NEW_QUESTION_DRAFT);

      /**
       * The API stopped guaranteeing a temporary password: a submission for an
       * address it already knows returns the inquiry without minting one. The
       * question is submitted either way, so an absent password means "land on
       * success signed out" rather than a failure — the same outcome the
       * swallowed login error already produced.
       */
      const temporaryPassword = result.data.temporaryPassword;

      if (temporaryPassword) {
        try {
          await loginUser({
            email: formData.email.trim().toLowerCase(),
            password: temporaryPassword,
          });
        } catch {}
      }

      replace('/(open)/new-question/success');
    } catch (error) {
      setIsSubmitting(false);

      toast.showError({
        title: 'Could not submit your question. Please try again.',
      });
    }
  });

  if (isSubmitting) {
    return <TransitionScreen message="Submitting your question ..." />;
  }

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      {isWeb ? null : (
        <Header
          contentScrollsUnderHeader
          isBackButton
          title="New Question"
          statusBarBackgroundColor={colors.background[0]}
        />
      )}
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin(
          'flex-col flex-grow pt-6 pb-6 web:pt-6 web:pb-10',
          SCREEN_COLUMN.content
        )}>
        <VStack className="mb-6">
          <Text size="xl" className="mb-3 font-bold text-typography-900">
            What is your email address?
          </Text>
        </VStack>
        <VStack space="md" className="mb-4">
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.email?.message}>
                <FormControlLabel className="mb-1.5">
                  <FormControlLabelText size="sm" className="font-medium text-typography-900">
                    Email address*
                  </FormControlLabelText>
                </FormControlLabel>
                <Input>
                  <InputField
                    placeholder="Enter your email address"
                    placeholderTextColor={colors.typography[600]}
                    style={{ color: colors.typography[900] }}
                    className="text-base"
                    value={value}
                    onChangeText={(text: string) => {
                      if (errors.email?.message) {
                        clearErrors('email');

                        setEmailExistsError(false);
                      }

                      onChange(text.toLowerCase());
                    }}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    enterKeyHint="done"
                    returnKeyType="done"
                    onSubmitEditing={onSubmit}
                  />
                </Input>
                <FormControlError className="mt-2 items-start">
                  <FormControlErrorIcon className="mt-0.5" size="xs" as={AlertCircleIcon} />
                  <FormControlErrorText size="xs">
                    {emailExistsError ? (
                      <>
                        This email address already has an account. Please{' '}
                        <LinkText
                          onPress={() =>
                            navigate(
                              `/(open)/login?redirect_path=${encodeURIComponent('/new-question')}`
                            )
                          }
                          size="xs"
                          className="text-typography-error data-[hover=true]:text-typography-error">
                          log in
                        </LinkText>{' '}
                        to finish your inquiry.
                      </>
                    ) : (
                      errors.email?.message
                    )}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />
        </VStack>
        <VStack className="flex-1" />
        <Button
          size="xl"
          className="w-full gap-x-2 web:mt-6"
          onPress={onSubmit}
          disabled={isPending}>
          {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText size="md">Submit</ButtonText>
        </Button>
        <HStack className="mt-4 flex-wrap justify-start">
          <Text size="sm" className="text-typography-900">
            Already have an account?{' '}
          </Text>
          <Link
            onPress={() =>
              navigate(`/(open)/login?redirect_path=${encodeURIComponent('/new-question')}`)
            }>
            <LinkText
              size="sm"
              className="font-bold text-primary-500 data-[hover=true]:text-primary-600">
              Sign in
            </LinkText>
          </Link>
        </HStack>
      </KeyboardAwareScrollView>
    </VStack>
  );
}

export default function GetContactInformation() {
  if (isWeb) {
    return (
      <VStack className="h-screen w-full items-center justify-center">
        <VStack className="w-full px-5" style={{ maxWidth: 540 }}>
          <NewQuestionGetContactInformationContent />
        </VStack>
      </VStack>
    );
  }

  return <NewQuestionGetContactInformationContent />;
}
